import {
  ESdChangeReason,
  IComponent,
  IDisplayItemProperty,
  IFillStyle,
  ILanguageModel,
  IProject,
  IScreenDesignDto,
  IStylingBorder,
  IStylingPosition,
  IStylingTypography
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import _ from "lodash";
import { CSSProperties } from "react";
import { ScreenService } from "../../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaColorPicker from "../../../ui-kit/kuika-color-picker/kuika-color-picker";
import "./add-button-modal.scss";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";

export interface IAddButtonModalProps {
  button: IScreenDesignDto | null;
  isEdit: boolean;
  showModal: boolean;
  onClose: () => void;
  /** Fires after a successful create (not edit), once the screen list is refreshed — before onClose. */
  onCreated?: (button: IScreenDesignDto) => void;
}

export interface IAddButtonModalState {
  isAddButtonInProgress: boolean;
  /** Where Save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  //
  borderStyles: IStylingBorder[];
  positionStyles: IStylingPosition[];
  fillStyles: IFillStyle[];
  typographyStyles: IStylingTypography[];
  //
  previewButtonStyle: CSSProperties;
  //
  name: string;
  label: string;
  textColor: string;
  backgroundColor: string;
  fontSize: number;
  lineHeight: number;
  //
  isMultiLanguageLabelEnabled: boolean;
  labelsByLanguage: { [key: string]: string };
}

class AddButtonModal extends PureSdComponent<IAddButtonModalProps & IWithSDProps, IAddButtonModalState> {
  static readonly initialState: IAddButtonModalState = {
    isAddButtonInProgress: false,
    saveStatus: "idle",
    //
    borderStyles: [],
    positionStyles: [],
    fillStyles: [],
    typographyStyles: [],
    //
    previewButtonStyle: {},
    //
    name: "",
    label: "",
    textColor: "#ffffff",
    backgroundColor: "#0358ff",
    fontSize: 16,
    lineHeight: 22,
    //
    isMultiLanguageLabelEnabled: false,
    labelsByLanguage: {}
  };

  defaultLanguage: ILanguageModel | null = null;

  constructor(props: IAddButtonModalProps & IWithSDProps) {
    super(props);
    this.state = { ...AddButtonModal.initialState };
  }

  getDefaultStyles = () => {
    const project: IProject = this.props.sd.project;
    const buttonDefinition: IComponent | undefined = project.componentSetObj.components.find(
      (c) => c.name === "Button" && !c.isDeprecated
    );

    if (!buttonDefinition) {
      return {
        borderStyles: [],
        positionStyles: [],
        fillStyles: [],
        typographyStyles: []
      };
    }

    const defaultStylingBorder: string[] =
      buttonDefinition.defaultStylingBorder?.split("|")?.map((s) => s.toLowerCase()) || [];
    const defaultStylingPosition: string[] =
      buttonDefinition.defaultStylingPosition?.split("|")?.map((s) => s.toLowerCase()) || [];
    const defaultStylingFill: string[] =
      buttonDefinition.defaultStylingFill?.split("|")?.map((s) => s.toLowerCase()) || [];
    const defaultStylingTypography: string[] =
      buttonDefinition.defaultStylingTypography?.split("|")?.map((s) => s.toLowerCase()) || [];

    // Get default border styles
    const borderStyles = project.stylingBorder.filter(
      (style: IStylingBorder) => style.borderId && defaultStylingBorder.includes(style.borderId?.toLowerCase())
    );

    const positionStyles = project.stylingPosition.filter(
      (style: IStylingPosition) => style.positionId && defaultStylingPosition.includes(style.positionId?.toLowerCase())
    );

    const fillStyles = project.stylingFill.filter(
      (style: IFillStyle) => style.fillId && defaultStylingFill.includes(style.fillId?.toLowerCase())
    );

    const typographyStyles = project.stylingTypography.filter(
      (style: IStylingTypography) => style.typoId && defaultStylingTypography.includes(style.typoId?.toLowerCase())
    );

    return {
      borderStyles,
      positionStyles,
      fillStyles,
      typographyStyles
    };
  };

  convertToHexFromRgb = (rgba: string): string => {
    const rgbaArr = rgba.split(",");
    const r: number = parseInt(rgbaArr[0].split("(")[1], 10);
    const g: number = parseInt(rgbaArr[1], 10);
    const b: number = parseInt(rgbaArr[2], 10);

    const rHex: string = _.padStart(r.toString(16), 2, "0");
    const gHex: string = _.padStart(g.toString(16), 2, "0");
    const bHex: string = _.padStart(b.toString(16), 2, "0");

    return `#${rHex}${gHex}${bHex}`;
  };

  componentDidMount() {
    const defaultLanguage: ILanguageModel | undefined = this.props.sd.project.languages.find((x) => x.isDefault);

    if (defaultLanguage) {
      this.defaultLanguage = defaultLanguage;
    }

    this.resetPreviewButtonStyles();
  }

  componentDidUpdate(prevProps: IAddButtonModalProps & IWithSDProps) {
    if (prevProps.showModal !== this.props.showModal && this.props.showModal) {
      if (this.props.button && this.props.isEdit) {
        const screenModel = JSON.parse(this.props.button?.json);
        const displayItems = screenModel?.displayItems;
        const buttonDi = ScreenDesignHelper.findDisplayItemByComponentName("Button", displayItems);

        if (buttonDi) {
          const properties: IDisplayItemProperty[] = buttonDi?.item?.properties;
          const labelProperty: IDisplayItemProperty | undefined = properties?.find((p) => p.name === "label");

          const defaultStyles = this.getDefaultStyles();

          const fontSize: number =
            buttonDi.styleObj?.text?.fontSize ?? defaultStyles?.typographyStyles?.at(-1)?.fontSize ?? 16;
          const lineHeight: number =
            buttonDi.styleObj?.text?.lineHeight ?? defaultStyles?.typographyStyles?.at(-1)?.lineHeight ?? 22;
          let textColor: string =
            buttonDi.styleObj?.text?.color ?? defaultStyles?.typographyStyles?.at(-1)?.color ?? "#ffffff";
          let backgroundColor: string =
            buttonDi.styleObj?.fill?.color ?? defaultStyles?.fillStyles?.at(-1)?.color ?? "#0358ff";

          if (textColor.startsWith("rgb")) {
            textColor = this.convertToHexFromRgb(textColor);
          }

          if (backgroundColor.startsWith("rgb")) {
            backgroundColor = this.convertToHexFromRgb(backgroundColor);
          }

          this.setState({
            name: this.props.button.name,
            label: labelProperty?.symbol.fixedValue ?? "",
            borderStyles: defaultStyles.borderStyles,
            positionStyles: defaultStyles.positionStyles,
            fillStyles: defaultStyles.fillStyles,
            typographyStyles: defaultStyles.typographyStyles,
            fontSize,
            lineHeight,
            textColor,
            backgroundColor
          });
        }
      }
    }
  }

  resetPreviewButtonStyles = () => {
    const defaultStyles = this.getDefaultStyles();

    this.setState(
      {
        borderStyles: defaultStyles.borderStyles,
        positionStyles: defaultStyles.positionStyles,
        fillStyles: defaultStyles.fillStyles,
        typographyStyles: defaultStyles.typographyStyles
      },
      () =>
        this.setPreviewButtonStyles(
          this.state.borderStyles,
          this.state.positionStyles,
          this.state.fillStyles,
          this.state.typographyStyles
        )
    );
  };

  setPreviewButtonStyles = (
    borderStyles: IStylingBorder[],
    positionStyles: IStylingPosition[],
    fillStyles: IFillStyle[],
    typographyStyles: IStylingTypography[]
  ) => {
    let previewButtonStyle: CSSProperties = {};
    let backgroundColor: string = "#0358ff";
    let fontSize: number = 16;
    let lineHeight: number = 22;

    for (const style of borderStyles) {
      if (style.borderColor) {
        previewButtonStyle.borderColor = style.borderColor;
      }

      if (style.borderTopWidth) {
        previewButtonStyle.borderWidth = style.borderTopWidth;
      }

      if (style.borderLeftWidth) {
        previewButtonStyle.borderWidth = style.borderLeftWidth;
      }

      if (style.borderRightWidth) {
        previewButtonStyle.borderWidth = style.borderRightWidth;
      }

      if (style.borderBottomWidth) {
        previewButtonStyle.borderWidth = style.borderBottomWidth;
      }

      if (style.borderTopLeftRadius) {
        previewButtonStyle.borderTopLeftRadius = style.borderTopLeftRadius;
      }

      if (style.borderTopRightRadius) {
        previewButtonStyle.borderTopRightRadius = style.borderTopRightRadius;
      }

      if (style.borderBottomLeftRadius) {
        previewButtonStyle.borderBottomLeftRadius = style.borderBottomLeftRadius;
      }

      if (style.borderBottomRightRadius) {
        previewButtonStyle.borderBottomRightRadius = style.borderBottomRightRadius;
      }

      if (style.borderStyle) {
        previewButtonStyle.borderStyle = style.borderStyle;
      }

      if (style.borderColor) {
        previewButtonStyle.borderColor = style.borderColor;
      }
    }

    for (const style of positionStyles) {
      if (style.height) {
        previewButtonStyle.height = style.height;
      }

      if (style.width) {
        previewButtonStyle.width = style.width;
      }

      if (style.paddingTop) {
        previewButtonStyle.paddingTop = style.paddingTop;
      }

      if (style.paddingLeft) {
        previewButtonStyle.paddingLeft = style.paddingLeft;
      }

      if (style.paddingRight) {
        previewButtonStyle.paddingRight = style.paddingRight;
      }

      if (style.paddingBottom) {
        previewButtonStyle.paddingBottom = style.paddingBottom;
      }

      if (style.marginTop) {
        previewButtonStyle.marginTop = style.marginTop;
      }

      if (style.marginLeft) {
        previewButtonStyle.marginLeft = style.marginLeft;
      }

      if (style.marginRight) {
        previewButtonStyle.marginRight = style.marginRight;
      }

      if (style.marginBottom) {
        previewButtonStyle.marginBottom = style.marginBottom;
      }

      if (style.maxWidth) {
        previewButtonStyle.maxWidth = style.maxWidth;
      }

      if (style.minWidth) {
        previewButtonStyle.minWidth = style.minWidth;
      }

      if (style.maxHeight) {
        previewButtonStyle.maxHeight = style.maxHeight;
      }

      if (style.minHeight) {
        previewButtonStyle.minHeight = style.minHeight;
      }
    }

    for (const style of fillStyles) {
      if (style.color) {
        previewButtonStyle.backgroundColor = style.color;
        backgroundColor = style.color.startsWith("rgb") ? this.convertToHexFromRgb(style.color) : style.color;
      }
    }

    for (const style of typographyStyles) {
      if (style.fontSize) {
        fontSize = style.fontSize;
      }

      if (style.lineHeight) {
        lineHeight = style.lineHeight;
      }

      if (style.fontWeight) {
        previewButtonStyle.fontWeight = style.fontWeight;
      }

      if (style.fontStyle) {
        previewButtonStyle.fontStyle = style.fontStyle;
      }
    }

    this.setState({ previewButtonStyle, backgroundColor, fontSize, lineHeight });
  };

  private getIsFontSizeValid = (): boolean => {
    if (Number.isNaN(this.state.fontSize)) {
      return false;
    }

    if (!Number.isFinite(this.state.fontSize)) {
      return false;
    }

    return this.state.fontSize > 0;
  };

  private getIsLineHeightValid = (): boolean => {
    if (Number.isNaN(this.state.fontSize)) {
      return false;
    }

    if (!Number.isFinite(this.state.fontSize)) {
      return false;
    }

    return this.state.lineHeight > 0;
  };

  private getIsAddButtonDisabled = (): boolean => {
    if (this.state.isAddButtonInProgress) {
      return true;
    }

    if (_.isEmpty(this.state.label?.trim())) {
      return true;
    }

    if (_.isEmpty(this.state.name?.trim())) {
      return true;
    }

    if (!this.getIsFontSizeValid()) {
      return true;
    }

    if (!this.getIsLineHeightValid()) {
      return true;
    }

    return false;
  };

  /**
   * Save's state machine: the ✓ is held on the button, then the dialog closes — the button it
   * saved is already selected on the interaction row behind it. `canStart` keeps a second Save
   * (a duplicate button) out while the ✓ is up.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  private onClickAddButton = async () => {
    if (!this.saveStatus.canStart) return;
    try {
      this.saveStatus.start();
      this.setState({ isAddButtonInProgress: true });

      let response: AxiosResponse<IScreenDesignDto>;

      if (this.props.isEdit) {
        if (!this.props.button) {
          this.saveStatus.reset();
          return;
        }

        response = await ScreenService.UpdateButtonComponent({
          id: this.props.button.id,
          name: this.state.name,
          label: this.state.label,
          textColor: this.state.textColor,
          backgroundColor: this.state.backgroundColor,
          fontSize: this.state.fontSize,
          lineHeight: this.state.lineHeight,
          isMultiLanguageLabelEnabled: this.state.isMultiLanguageLabelEnabled,
          labelsByLanguage: Object.entries(this.state.labelsByLanguage).map(([name, value]) => {
            const languageIdentifier: string | undefined = this.props.sd.project.languages?.find(
              (l) => l.shortName === name
            )?.name;
            return { name: languageIdentifier ?? name, value };
          })
        });
      } else {
        response = await ScreenService.CreateButtonComponent({
          name: this.state.name,
          label: this.state.label,
          textColor: this.state.textColor,
          backgroundColor: this.state.backgroundColor,
          fontSize: this.state.fontSize,
          lineHeight: this.state.lineHeight,
          isMultiLanguageLabelEnabled: this.state.isMultiLanguageLabelEnabled,
          labelsByLanguage: Object.entries(this.state.labelsByLanguage).map(([name, value]) => {
            const languageIdentifier: string | undefined = this.props.sd.project.languages?.find(
              (l) => l.shortName === name
            )?.name;
            return { name: languageIdentifier ?? name, value };
          })
        });
      }

      if (response.status === 200) {
        // Refresh before notifying/closing so the caller's option list already contains the new button.
        await this.props.sd.setScreenList();
        if (!this.props.isEdit && this.props.onCreated) {
          this.props.onCreated(response.data);
        }
        this.saveStatus.succeed((): void => this.onClose());
      } else {
        await this.props.sd.setScreenList();
        this.saveStatus.fail();
      }
    } catch (e) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isAddButtonInProgress: false });
    }
  };

  private onSwitchChange = (checked: boolean): void => {
    if (checked) {
      const labelsByLanguage: { [key: string]: string } = {};

      if (this.props.sd.project.languages) {
        for (const language of this.props.sd.project.languages) {
          if (this.defaultLanguage?.name === language.name) {
            labelsByLanguage[language.shortName] = this.state.label;
          } else {
            labelsByLanguage[language.shortName] = "";
          }
        }
      }

      this.setState({ isMultiLanguageLabelEnabled: true, labelsByLanguage });
    } else {
      this.setState({ isMultiLanguageLabelEnabled: false });
    }
  };

  private onChangeLabelByLanguage = (language: string, label: string) => {
    const labelsByLanguage = { ...this.state.labelsByLanguage };
    labelsByLanguage[language] = label;

    if (this.defaultLanguage?.shortName === language) {
      this.setState({ label });
    }

    this.setState({ labelsByLanguage });
  };

  private renderMultiLanguageRows = () => {
    const multiLanguageRowCountIn2Columns: number = Math.ceil(Object.keys(this.state.labelsByLanguage).length / 2);
    const multiLanguageRows: JSX.Element[] = [];
    const languages: string[] = Object.keys(this.state.labelsByLanguage);

    // Create rows in 2 columns by languages
    for (let i = 0; i < multiLanguageRowCountIn2Columns; i++) {
      const language1: string = languages[i * 2];
      const language2: string | undefined = languages[i * 2 + 1];

      multiLanguageRows.push(
        <div className="kuika-add-button-modal__grid" key={`ml-row-${language1}`}>
          <div className="kuika-add-button-modal__field">
            <KuikaSmallText className="kuika-add-button-modal__label">{language1} Label</KuikaSmallText>
            <KuikaInput
              size="md"
              value={this.state.labelsByLanguage[language1]}
              onChange={(_e, value) => this.onChangeLabelByLanguage(language1, String(value ?? ""))}
            />
          </div>
          {language2 && (
            <div className="kuika-add-button-modal__field">
              <KuikaSmallText className="kuika-add-button-modal__label">{language2} Label</KuikaSmallText>
              <KuikaInput
                size="md"
                value={this.state.labelsByLanguage[language2]}
                onChange={(_e, value) => this.onChangeLabelByLanguage(language2, String(value ?? ""))}
              />
            </div>
          )}
        </div>
      );
    }

    return multiLanguageRows;
  };

  onClose = () => {
    this.saveStatus.reset();
    this.setState({ ...AddButtonModal.initialState });
    this.resetPreviewButtonStyles();
    this.props.onClose();
  };

  // KModal doesn't block closing while a save is in flight (the previous modal here honoured
  // `isLoading` for that by default) — guard it here so the dialog can't be closed out from
  // under an in-flight save.
  private handleCloseModal = (): void => {
    // Nor while its ✓ is being held: the dialog closes itself once it has been seen.
    if (this.state.isAddButtonInProgress || !this.saveStatus.canStart) {
      return;
    }

    this.onClose();
  };

  render() {
    const footer = (
      <KModalFooter
        onCancel={this.handleCloseModal}
        cancelTestId="addButtonModalCancelButton"
        submitLabel="Save"
        onSubmit={this.onClickAddButton}
        disabled={this.getIsAddButtonDisabled()}
        status={this.state.saveStatus}
      />
    );

    return (
      <KModal
        showModal={this.props.showModal}
        header={this.props.isEdit ? "Edit Button" : "Add Button"}
        closeModal={this.handleCloseModal}
        footer={footer}
        width={600}
        isLoading={this.state.isAddButtonInProgress}
      >
        <div className="kuika-add-button-modal">
          <div className="kuika-add-button-modal__preview-block">
            <KuikaSmallText className="kuika-add-button-modal__label">Preview</KuikaSmallText>
            <div className="kuika-add-button-modal__preview">
              {/* A preview of the user's own button, so it wears their colours and metrics
                  rather than the kit's — a KuikaButton here would overrule both. */}
              <button
                type="button"
                data-testid="previewButton"
                className="kuika-add-button-modal__preview-button"
                style={{
                  ...this.state.previewButtonStyle,
                  backgroundColor: this.state.backgroundColor,
                  color: this.state.textColor,
                  fontSize: `${this.state.fontSize}px`,
                  lineHeight: `${this.state.lineHeight}px`
                }}
              >
                {this.state.label && this.state.label !== "" ? this.state.label : "Button"}
              </button>
            </div>
          </div>

          <div className="kuika-add-button-modal__grid">
            <div className="kuika-add-button-modal__field">
              <KuikaSmallText className="kuika-add-button-modal__label">Name</KuikaSmallText>
              <KuikaInput
                data-testid="addButtonModalNameInput"
                size="md"
                value={this.state.name}
                onChange={(_e, value) => this.setState({ name: String(value ?? "") })}
              />
            </div>
            {!this.state.isMultiLanguageLabelEnabled && (
              <div className="kuika-add-button-modal__field">
                <KuikaSmallText className="kuika-add-button-modal__label">Label</KuikaSmallText>
                <KuikaInput
                  data-testid="addButtonModalLabelInput"
                  size="md"
                  value={this.state.label}
                  onChange={(_e, value) => this.setState({ label: String(value ?? "") })}
                />
              </div>
            )}
          </div>
          {this.state.isMultiLanguageLabelEnabled && this.renderMultiLanguageRows()}

          <div className="kuika-add-button-modal__grid">
            <div className="kuika-add-button-modal__field">
              <KuikaSmallText className="kuika-add-button-modal__label">Text Color</KuikaSmallText>
              <KuikaColorPicker
                color={this.state.textColor}
                onChange={(color: string) => this.setState({ textColor: color })}
                position="top"
              />
            </div>
            <div className="kuika-add-button-modal__field">
              <KuikaSmallText className="kuika-add-button-modal__label">Background Color</KuikaSmallText>
              <KuikaColorPicker
                color={this.state.backgroundColor}
                onChange={(color: string) => this.setState({ backgroundColor: color })}
                position="top"
              />
            </div>
          </div>

          <div className="kuika-add-button-modal__grid">
            <div className="kuika-add-button-modal__field">
              <KuikaSmallText className="kuika-add-button-modal__label">Font Size</KuikaSmallText>
              <KuikaInput
                type="number"
                size="md"
                value={this.state.fontSize}
                onChange={(_e, value) => this.setState({ fontSize: Number(value) })}
                rightUnit="px"
              />
            </div>
            <div className="kuika-add-button-modal__field">
              <KuikaSmallText className="kuika-add-button-modal__label">Line Height</KuikaSmallText>
              <KuikaInput
                type="number"
                size="md"
                value={this.state.lineHeight}
                onChange={(_e, value) => this.setState({ lineHeight: Number(value) })}
                rightUnit="px"
              />
            </div>
          </div>
        </div>
      </KModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason, nextProps: IAddButtonModalProps & IWithSDProps): boolean => {
    return this.props.showModal !== nextProps.showModal;
  };
}

const addButtonModal = withSD<IAddButtonModalProps>(AddButtonModal);
export { addButtonModal as AddButtonModal };
